import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { requestPasswordReset, resetPassword } from '../repository/authRepository';

const PasswordReset: React.FC = () => {
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token');
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [variant, setVariant] = useState<'success' | 'error'>('success');
  const [isLoading, setIsLoading] = useState(false);

  const handleRequestReset = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      const result = await requestPasswordReset({ username });

      if ('error' in result) {
        setMessage(result.error || 'Failed to send reset email');
        setVariant('error');
      } else {
        setMessage('Password reset email sent! Check your inbox for the link.');
        setVariant('success');
        setUsername('');
      }
    } catch (error) {
      setMessage('An error occurred while sending the reset email');
      setVariant('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (event: React.FormEvent) => {
    event.preventDefault();

    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match');
      setVariant('error');
      return;
    }

    if (newPassword.length < 8) {
      setMessage('Password must be at least 8 characters long');
      setVariant('error');
      return;
    }

    setIsLoading(true);

    try {
      if (!resetToken) {
        setMessage('No reset token provided');
        setVariant('error');
        return;
      }

      const result = await resetPassword({ reset_token: resetToken, new_password: newPassword });

      if ('error' in result) {
        setMessage(result.error || 'Failed to update password');
        setVariant('error');
      } else {
        setMessage('Password updated successfully! Redirecting to login...');
        setVariant('success');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (error) {
      setMessage('An error occurred while updating the password');
      setVariant('error');
    } finally {
      setIsLoading(false);
    }
  };

  if (resetToken) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <h1 className="auth-title">Set a new password</h1>
          <p className="helper-text">Choose a new password for your account.</p>

          {message && <div className={`alert ${variant === 'error' ? 'error' : ''}`}>{message}</div>}

          <form onSubmit={handleResetPassword} className="grid" style={{ gap: '1rem' }}>
            <label className="field">
              <span className="field-label">New password</span>
              <input
                className="input"
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                required
                minLength={8}
              />
            </label>

            <label className="field">
              <span className="field-label">Confirm password</span>
              <input
                className="input"
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
                minLength={8}
              />
            </label>

            <button className="button" type="submit" disabled={isLoading}>
              {isLoading ? 'Updating...' : 'Update password'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <h1 className="auth-title">Reset your password</h1>
        <p className="helper-text">Enter your username and we will email you a reset link.</p>

        {message && <div className={`alert ${variant === 'error' ? 'error' : ''}`}>{message}</div>}

        <form onSubmit={handleRequestReset} className="grid" style={{ gap: '1rem' }}>
          <label className="field">
            <span className="field-label">Username</span>
            <input
              className="input"
              type="text"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              required
            />
          </label>

          <button className="button" type="submit" disabled={isLoading}>
            {isLoading ? 'Sending...' : 'Send reset link'}
          </button>
        </form>

        <div className="helper-text" style={{ marginTop: '1rem' }}>
          <Link to="/login">Back to login</Link>
        </div>
      </div>
    </div>
  );
};

export default PasswordReset;
